/**
 * Delete confirmation for one CMDBuild card.
 *
 * CMDBuild enforces no referential integrity, so the server refuses a delete
 * while inbound references exist and names them. This dialog shows that list
 * BEFORE the attempt (it comes from the single-card read), which is the whole
 * point: the operator sees what they would have to detach.
 *
 * Cascade is offered only when *every* blocker is a purely dependent card — a
 * Stücklisteneintrag, an Abweichung, a Benachrichtigung. Those exist solely
 * because of the reference. A blocker with an identity of its own (a Produkt-
 * instanz under a Kunde) is never cascaded; the delete simply stays refused.
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { Modal } from '../../components/Modal';
import Spinner from '../../components/Spinner';
import { errorMessage } from '../../lib/api';
import type { CmdbCardBlocker, CmdbCardRow, CmdbClassMeta } from '../../lib/types';

/** Classes whose cards only exist for their reference — safe to cascade. */
const DEPENDENT_CLASSES = new Set(['ProduktVersionKomponente', 'InstanzAbweichung', 'Benachrichtigung']);

export interface DeleteCardModalProps {
  meta: CmdbClassMeta;
  row: CmdbCardRow;
  blockers: readonly CmdbCardBlocker[];
  onConfirm: (cascade: boolean) => Promise<unknown>;
  onClose: () => void;
}

export function DeleteCardModal({
  meta,
  row,
  blockers,
  onConfirm,
  onClose,
}: DeleteCardModalProps): ReactNode {
  const [busy, setBusy] = useState(false);
  const [cascade, setCascade] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cascadable = useMemo(
    () => blockers.length > 0 && blockers.every((b) => DEPENDENT_CLASSES.has(b.class)),
    [blockers],
  );
  const blocked = blockers.length > 0 && !cascadable;

  const confirm = useCallback(async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(cascade);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }, [cascade, onConfirm]);

  return (
    <Modal
      title={`${meta.label} löschen`}
      subtitle={row.label}
      dismissable={!busy}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} disabled={busy}>
            Abbrechen
          </button>
          <button
            type="button"
            className="primary"
            onClick={confirm}
            disabled={busy || blocked || (cascadable && !cascade)}
          >
            {busy ? <Spinner label="Löscht…" /> : 'Endgültig löschen'}
          </button>
        </>
      }
    >
      {error ? <div className="banner error">{error}</div> : null}

      {blockers.length === 0 ? (
        <p>
          Kein anderer Datensatz verweist auf diesen Eintrag. Das Löschen ist nicht umkehrbar.
        </p>
      ) : (
        <>
          <p>
            {blockers.length === 1
              ? 'Ein Datensatz referenziert diesen Eintrag:'
              : `${blockers.length} Datensätze referenzieren diesen Eintrag:`}
          </p>
          <ul className="stamm-blockers">
            {blockers.slice(0, 12).map((blocker) => (
              <li key={`${blocker.class}-${blocker.id}`}>
                <span className="muted">{blocker.class_label}</span> {blocker.label}{' '}
                <span className="muted">({blocker.attribute_label})</span>
              </li>
            ))}
            {blockers.length > 12 ? (
              <li className="muted">… und {blockers.length - 12} weitere</li>
            ) : null}
          </ul>
          {cascadable ? (
            <label className="check">
              <input
                type="checkbox"
                checked={cascade}
                disabled={busy}
                onChange={(event) => setCascade(event.target.checked)}
              />
              <span>
                Diese abhängigen Datensätze mitlöschen. Sie bestehen nur wegen dieses Verweises.
              </span>
            </label>
          ) : (
            <div className="banner warn">
              Löschen nicht möglich, solange diese Datensätze bestehen. Hängen Sie sie zuerst um
              oder löschen Sie sie.
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

export default DeleteCardModal;
